import React, { useState, useEffect } from 'react';
import { WhatsappLogo, X, Microphone, Camera, ChatCircleText, PaperPlaneTilt } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WhatsAppFloatingButton = ({ user }) => {
  const [botInfo, setBotInfo] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sendingWelcome, setSendingWelcome] = useState(false);

  useEffect(() => {
    const fetchBotInfo = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/whatsapp/bot-info`, {
          withCredentials: true
        });
        setBotInfo(response.data);
      } catch (error) {
        console.error('Error fetching WhatsApp bot info:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchBotInfo();
    }
  }, [user]);

  // Don't show if loading, not active, or user not authenticated
  if (loading || !botInfo || !botInfo.is_subscription_active) {
    return null;
  }

  const handleWhatsAppClick = () => {
    if (botInfo.wa_me_link) {
      // Pre-fill message based on whether user has registered their number
      let message = "Olá Aretê! 👋";
      if (!botInfo.user_whatsapp_registered) {
        message = "Olá! Gostaria de começar a usar o bot da Aretê!";
      }
      window.open(`${botInfo.wa_me_link}?text=${encodeURIComponent(message)}`, '_blank');
    }
  };

  const handleSendWelcome = async () => {
    if (!botInfo.user_whatsapp_registered) {
      toast.error('Cadastre seu WhatsApp no Perfil primeiro!');
      return;
    }

    setSendingWelcome(true);
    try {
      await axios.post(`${BACKEND_URL}/api/whatsapp/send-welcome`, {}, {
        withCredentials: true
      });
      toast.success('Mensagem de boas-vindas enviada! Verifique seu WhatsApp 📱');
    } catch (error) {
      console.error('Error sending welcome:', error);
      const msg = error.response?.data?.detail || 'Erro ao enviar mensagem';
      toast.error(msg);
    } finally {
      setSendingWelcome(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        {/* Expanded Menu */}
        {isExpanded && (
          <div className="bg-white rounded-2xl shadow-2xl p-4 mb-2 w-80 border border-gray-100 animate-in slide-in-from-bottom-2 duration-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <WhatsappLogo size={24} weight="fill" className="text-green-500" />
                <span className="font-semibold text-gray-800">Aretê Bot</span>
              </div>
              <button 
                onClick={() => setIsExpanded(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={18} className="text-gray-500" />
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Gerencie suas finanças e tarefas pelo WhatsApp! 
            </p>

            {/* Features */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <ChatCircleText size={18} className="text-green-500" />
                <span>Mensagens de texto</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Microphone size={18} className="text-green-500" />
                <span>Áudios (transcrição automática)</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Camera size={18} className="text-green-500" />
                <span>Fotos de notas e recibos</span>
              </div>
            </div>

            {/* Warning if WhatsApp not registered */}
            {!botInfo.user_whatsapp_registered && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-yellow-800">
                  ⚠️ Cadastre seu WhatsApp no <strong>Perfil</strong> para vincular sua conta ao bot.
                </p>
              </div>
            )}

            {/* Buttons */}
            <div className="space-y-2">
              {/* Send Welcome Button - Only if registered */}
              {botInfo.user_whatsapp_registered && (
                <button
                  onClick={handleSendWelcome}
                  disabled={sendingWelcome}
                  className="w-full bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-medium py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  <PaperPlaneTilt size={20} weight="fill" />
                  {sendingWelcome ? 'Enviando...' : 'Receber mensagem de teste'}
                </button>
              )}

              {/* CTA Button */}
              <button
                onClick={handleWhatsAppClick}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors shadow-lg shadow-green-500/30"
              >
                <WhatsappLogo size={22} weight="fill" />
                Abrir WhatsApp
              </button>
            </div>

            {/* Registered number info */}
            {botInfo.user_whatsapp_registered && (
              <p className="text-xs text-gray-400 text-center mt-3">
                Vinculado ao: {botInfo.user_whatsapp?.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, '+$1 ($2) $3-$4')}
              </p>
            )}
          </div>
        )}

        {/* Main Floating Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`
            w-14 h-14 rounded-full shadow-lg flex items-center justify-center
            transition-all duration-300 hover:scale-110
            ${isExpanded 
              ? 'bg-gray-700 hover:bg-gray-800' 
              : 'bg-green-500 hover:bg-green-600 neo-wiggle'
            }
          `}
          title="Abrir WhatsApp Bot"
        >
          {isExpanded ? (
            <X size={26} weight="bold" className="text-white" />
          ) : (
            <WhatsappLogo size={30} weight="fill" className="text-white" />
          )}
        </button>
      </div>

      {/* Wiggle animation style */}
      <style>{`
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          15% { transform: rotate(-12deg); }
          30% { transform: rotate(10deg); }
          45% { transform: rotate(-8deg); }
          60% { transform: rotate(6deg); }
          75% { transform: rotate(-4deg); }
          90% { transform: rotate(2deg); }
        }
        
        .neo-wiggle {
          animation: wiggle 0.6s ease-in-out;
          animation-delay: 3s;
          animation-iteration-count: infinite;
          animation-duration: 0.6s;
          animation-timing-function: ease-in-out;
        }
        
        .neo-wiggle:hover {
          animation: none;
        }
        
        @keyframes wiggle-repeat {
          0%, 85%, 100% { transform: rotate(0deg); }
          87% { transform: rotate(-12deg); }
          89% { transform: rotate(10deg); }
          91% { transform: rotate(-8deg); }
          93% { transform: rotate(6deg); }
          95% { transform: rotate(-4deg); }
          97% { transform: rotate(2deg); }
        }
        
        .neo-wiggle {
          animation: wiggle-repeat 5s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default WhatsAppFloatingButton;
